import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { isNumber } from 'app/utils/types';
import { partitionArray, sort } from 'app/utils/array';
import { withBackgroundImage } from 'app/utils/style';
import Dota2BarrackIcon from 'app/icons/dota2_barrack.svg';
import Dota2KillIcon from 'app/icons/dota2_kill.svg';
import Dota2PositionIndicator from 'app/components/Dota2PositionIndicator';
import Dota2TowerIcon from 'app/icons/dota2_tower.svg';
import GameTimer from 'app/components/GameTimer';
import HeroesIcon from 'app/components/HeroesIcon';
import IconWithTooltip from 'app/components/IconWithTooltip';
import Tooltip from 'app/components/Tooltip';
import KDA from 'app/components/KDA';
import { usePlayerNameOverflow } from 'app/hooks/usePlayerNameOverflow';
import { getMap } from 'app/utils/entities/match_data';
import sharedStyle from 'app/components/MatchesRecap/style.module.scss';
import Dota2Map from 'app/components/Dota2Map';
import GoldAdvantageChart from 'app/components/GoldAdvantageChart';
import Error from 'app/components/Error';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

interface Props {
  game: DetailedGameDota2;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
}

const Dota2MatchRecap: FC<Props> = ({ game, opponent1, opponent2 }) => {
  const { t } = useTranslation();

  const gameMap = getMap(game);

  const { team1PlayerNamesRef, team2PlayerNamesRef, playersNamesOverflow } =
    usePlayerNameOverflow(5);

  // These objects are the same, just used to access the opponent depending on its number or its side
  const [dires, radiants] = partitionArray(game.opponents, opponent =>
    opponent.side === 'dire' ? 0 : 1
  );
  const dire = dires.at(0);
  const radiant = radiants.at(0);
  const [gameOpponents1, gameOpponents2] = partitionArray(game.opponents, opponent =>
    opponent.id === opponent1?.id ? 0 : 1
  );
  const gameOpponent1 = gameOpponents1.at(0);
  const gameOpponent2 = gameOpponents2.at(0);

  const [direOpponentInfos, radiantOpponentInfos] =
    opponent1?.id === dire?.id ? [opponent1, opponent2] : [opponent2, opponent1];

  const winner = game.opponents.find(opponent => opponent.id === game.winner_id);

  const team1Players = sort(gameOpponent1?.players ?? [], player => player.position);
  const team2Players = sort(gameOpponent2?.players ?? [], player => player.position);

  if (game.opponents.length === 0) {
    return <Error type="no-data" />;
  }

  return (
    <>
      <div
        className={sharedStyle.scoresContainer}
        style={gameMap ? withBackgroundImage(getCDNUrl(gameMap.image_url)) : {}}
      >
        <div className={sharedStyle.killsContainer}>
          {isNumber(gameOpponent1?.kills) && (
            <>
              {gameOpponent1.kills}
              <IconWithTooltip
                Icon={Dota2KillIcon}
                label=""
                iconClassName={classNames(
                  sharedStyle.reversedIcon,
                  style[gameOpponent1.side ?? 'defaultColor']
                )}
              />
            </>
          )}
        </div>
        <div className={sharedStyle.killsContainer}>
          {isNumber(gameOpponent2?.kills) && (
            <>
              <IconWithTooltip
                Icon={Dota2KillIcon}
                label=""
                iconClassName={style[gameOpponent2.side ?? 'defaultColor']}
              />
              {gameOpponent2.kills}
            </>
          )}
        </div>
      </div>
      <div className={sharedStyle.matchStats}>
        <ul className={classNames(sharedStyle.teamStats, style.teamStats)}>
          <li>
            <IconWithTooltip
              Icon={Dota2BarrackIcon}
              label=""
              className={sharedStyle.teamStatsTooltip}
              iconClassName={classNames(
                sharedStyle.teamStatIcon,
                style[gameOpponent1?.side ?? 'defaultColor']
              )}
            />
            {gameOpponent1?.barracks}
          </li>
          <li>
            <IconWithTooltip
              Icon={Dota2TowerIcon}
              label=""
              className={sharedStyle.teamStatsTooltip}
              iconClassName={classNames(
                sharedStyle.teamStatIcon,
                style[gameOpponent1?.side ?? 'defaultColor']
              )}
            />
            {gameOpponent1?.towers}
          </li>
        </ul>
        <div className={sharedStyle.timerContainer}>
          {game.timer && <GameTimer timer={game.timer} videogame={game.videogame_slug} />}
        </div>
        <ul className={classNames(sharedStyle.teamStats, style.teamStats)}>
          <li>
            <IconWithTooltip
              Icon={Dota2TowerIcon}
              label=""
              className={sharedStyle.teamStatsTooltip}
              iconClassName={classNames(
                sharedStyle.teamStatIcon,
                style[gameOpponent2?.side ?? 'defaultColor']
              )}
            />
            {gameOpponent2?.towers}
          </li>
          <li>
            <IconWithTooltip
              Icon={Dota2BarrackIcon}
              label=""
              className={sharedStyle.teamStatsTooltip}
              iconClassName={classNames(
                sharedStyle.teamStatIcon,
                style[gameOpponent2?.side ?? 'defaultColor']
              )}
            />
            {gameOpponent2?.barracks}
          </li>
        </ul>
      </div>
      <div className={style.statsContainer}>
        <div className={style.mapContainer}>
          <h4 className={sharedStyle.statsContainerTitle}>{t('component_match-recap_moba_map')}</h4>
          <Dota2Map
            withBarracks
            dire={dire}
            radiant={radiant}
            winner={winner?.side}
            className={style.map}
          />
        </div>

        <GoldAdvantageChart
          goldAdvantage={game.radiant_gold_adv}
          goldAdvantageTeamInfo={radiantOpponentInfos}
          secondTeamInfo={direOpponentInfos}
          goldAvantageTeam="radiant"
        />

        <div className={style.playersContainer}>
          <h4 className={sharedStyle.statsContainerTitle}>
            {t('component_match-recap_moba_players')}
          </h4>
          <ul className={sharedStyle.playersList}>
            {Array(5)
              .fill(null)
              .map((_, position) => {
                const team1Player = team1Players.at(position);
                const team2Player = team2Players.at(position);
                return (
                  <li className={sharedStyle.playerLine} key={position}>
                    <div>
                      <Tooltip
                        content={team1Player?.name}
                        active={playersNamesOverflow[0][position]}
                      >
                        <div
                          className={sharedStyle.playerName}
                          ref={team1PlayerNamesRef.current[position]}
                        >
                          {team1Player?.name}
                        </div>
                      </Tooltip>
                      <KDA playerStats={team1Player} />
                    </div>
                    <div className={sharedStyle.playersRole}>
                      <HeroesIcon
                        heroes={team1Player ? [team1Player.hero] : []}
                        hidePercentage
                        iconClassName={sharedStyle.playerHero}
                        videogame="dota2"
                      />
                      <Dota2PositionIndicator position={position + 1} />
                      <HeroesIcon
                        heroes={team2Player ? [team2Player.hero] : []}
                        hidePercentage
                        iconClassName={sharedStyle.playerHero}
                        orientation="reversed"
                        videogame="dota2"
                      />
                    </div>
                    <div>
                      <Tooltip
                        content={team2Player?.name}
                        active={playersNamesOverflow[1][position]}
                      >
                        <div
                          className={sharedStyle.playerName}
                          ref={team2PlayerNamesRef.current[position]}
                        >
                          {team2Player?.name}
                        </div>
                      </Tooltip>
                      <KDA playerStats={team2Player} />
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    </>
  );
};

Dota2MatchRecap.displayName = 'Dota2MatchRecap';

export default Dota2MatchRecap;
